import type { components } from '@feedfm/clips-services-api';
import { useEffect, useRef, useState } from 'react';
import { CollectionSortBy, sortCollection } from '../util/collectionSort';

type Collection = components['schemas']['Collection'];
type Clip = components['schemas']['Clip'];

// Union type for what can be playing
type PlayingItem = Collection | Clip;

export interface PlayerState {
  /** What's currently loaded for playback - either a Collection or a single clip row */
  playingItem: PlayingItem | null;

  /** Whether the user intends to play back audio */
  playIntent: boolean;

  /** Current playback position in seconds */
  playbackPosition: number;

  /** Index of the currently playing track within a collection (or -1 for single clips) */
  currentlyPlayingIndex: number;

  /** Audio volume (0-100) */
  volume: number;

  /** Audio element duration in seconds (when loaded) */
  duration: number;

  /** The currently playing track (extracted from playingItem) */
  currentTrack: Clip | null;

  /** Set the audio volume (0-100) */
  setVolume: (volume: number) => void;

  /** Seeks to a specific position in the current track */
  seek: (position: number) => void;

  /** Play or pause the currently active item */
  togglePlayback: () => void;

  /** Click a collection to play it (or pause if it's actively playing) */
  clickCollection: (collection: Collection, sort: CollectionSortBy) => void;

  /**
   * Click an index within the collection to play it (or pause if it's actively playing). If another
   * track in the same collection is already playing, it will be stopped and the new track will
   * start playing.
   */
  clickCollectionClip: (
    collection: Collection,
    index: number,
    sort: CollectionSortBy,
  ) => void;

  /**
   * Play a single clip, optionally constrained to a [startMs, endMs] window of
   * the source audio (e.g. a custom clip previewing a slice of its parent
   * track). Clicking the clip that's already loaded toggles play/pause.
   */
  playClip: (
    clip: Clip,
    range?: { startMs: number; endMs: number },
  ) => void;

  /** Move to the next track in a collection */
  next: () => void;

  /** Move to the previous track in a collection */
  prev: () => void;

  /** Check if there's a next track available */
  hasNext: () => boolean;

  /** Check if there's a previous track available */
  hasPrev: () => boolean;

  /** Check if a specific clip is currently playing */
  isClipPlaying: (clipId: number) => boolean;

  /** Check if a specific collection is currently playing */
  isCollectionPlaying: (collectionId: number) => boolean;
}

export const useGlobalPlayer = (injectedVolume?: {
  volume: number;
  setVolume: (volume: number) => void;
}): PlayerState => {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // State
  const [playingItem, setPlayingItem] = useState<PlayingItem | null>(null);
  const [playIntent, setPlayIntent] = useState<boolean>(false);
  const [playbackPosition, setPlaybackPosition] = useState<number>(0);
  const [currentlyPlayingIndex, setCurrentlyPlayingIndex] =
    useState<number>(-1);
  const [volumeState, setVolumeState] = useState<number>(100);
  const [duration, setDuration] = useState<number>(0);
  const [currentTrack, setCurrentTrack] = useState<Clip | null>(null);
  // When set, the loaded clip plays only within this window of the source audio.
  const [playbackRange, setPlaybackRange] = useState<{
    startMs: number;
    endMs: number;
  } | null>(null);

  // If the caller injects volume management (e.g., StudioContext with localStorage), use that.
  // Otherwise fall back to internal state.
  const volume = injectedVolume ? injectedVolume.volume : volumeState;
  const setVolume = injectedVolume ? injectedVolume.setVolume : setVolumeState;

  // Initialize audio element
  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
    }

    const audio = audioRef.current;

    // Set up event listeners
    const handleLoadedMetadata = () => {
      setDuration(audio.duration || 0);
      // For a ranged clip, begin at the clip's start offset rather than 0.
      if (playbackRange) {
        audio.currentTime = playbackRange.startMs / 1000;
        setPlaybackPosition(playbackRange.startMs / 1000);
      }
      // Audio is ready to play
      if (playIntent) {
        audio.play().catch(console.error);
      }
    };

    const handleTimeUpdate = () => {
      // Enforce the clip window: stop and rewind to the start at the end handle.
      if (playbackRange && audio.currentTime * 1000 >= playbackRange.endMs) {
        audio.pause();
        audio.currentTime = playbackRange.startMs / 1000;
        setPlaybackPosition(playbackRange.startMs / 1000);
        setPlayIntent(false);
        return;
      }
      setPlaybackPosition(audio.currentTime);
    };

    const handleEnded = () => {
      setPlayIntent(false);
      setPlaybackPosition(0);

      // Auto-play next track if playing a collection and not at the end
      if (
        playingItem &&
        'clips' in playingItem &&
        currentlyPlayingIndex < playingItem.clips.length - 1
      ) {
        setCurrentlyPlayingIndex(currentlyPlayingIndex + 1);
        setPlayIntent(true);
      }
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [playIntent, currentlyPlayingIndex, playingItem, playbackRange]);

  // Handle play intent changes
  useEffect(() => {
    if (audioRef.current) {
      if (playIntent) {
        audioRef.current.play().catch(console.error);

        // Log playback beginning
        if (currentTrack) {
          const trackInfo =
            playingItem && 'clips' in playingItem
              ? {
                  collection: playingItem.collectionName,
                  track: `${currentlyPlayingIndex + 1} of ${playingItem.clips.length}`,
                  url: currentTrack.preSignedUrl,
                }
              : {
                  collection: 'Single Track',
                  track: '1 of 1',
                  url: currentTrack.preSignedUrl,
                };

          console.log(
            `▶️ Playback started: "${currentTrack.title}" by ${currentTrack.artist}`,
            trackInfo,
          );
        }
      } else {
        audioRef.current.pause();

        // Log pausing
        if (currentTrack) {
          const trackInfo =
            playingItem && 'clips' in playingItem
              ? {
                  collection: playingItem.collectionName,
                  track: `${currentlyPlayingIndex + 1} of ${playingItem.clips.length}`,
                  url: currentTrack.preSignedUrl,
                }
              : {
                  collection: 'Single Track',
                  track: '1 of 1',
                  url: currentTrack.preSignedUrl,
                };

          console.log(
            `⏸️ Playback paused: "${currentTrack.title}" by ${currentTrack.artist}`,
            trackInfo,
          );
        }
      }
    }
  }, [playIntent, currentTrack, playingItem, currentlyPlayingIndex]);

  // Handle volume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
    }
  }, [volume]);

  // Load current track into audio element
  useEffect(() => {
    if (audioRef.current && playingItem) {
      let audioUrl: string;

      if ('clips' in playingItem) {
        // Playing a collection - get current clip
        if (
          currentlyPlayingIndex >= 0 &&
          currentlyPlayingIndex < playingItem.clips.length
        ) {
          const clip = playingItem.clips[currentlyPlayingIndex];
          audioUrl = clip.preSignedUrl;
          setCurrentTrack(clip);

          // Log track loading
          console.log(`🎵 Loading track: "${clip.title}" by ${clip.artist}`, {
            collection: playingItem.collectionName,
            track: `${currentlyPlayingIndex + 1} of ${playingItem.clips.length}`,
            url: audioUrl,
          });
        } else {
          setCurrentTrack(null);
          return; // No valid clip to play
        }
      } else {
        // Playing a single clip
        audioUrl = playingItem.preSignedUrl;
        setCurrentTrack(playingItem);

        // Log track loading
        console.log(
          `🎵 Loading track: "${playingItem.title}" by ${playingItem.artist}`,
          {
            collection: 'Single Track',
            track: '1 of 1',
            url: audioUrl,
          },
        );
      }

      audioRef.current.src = audioUrl;
      audioRef.current.load();
    }
  }, [playingItem, currentlyPlayingIndex]);

  // Helper functions
  const playCollection = (
    collection: Collection,
    startIndex: number = 0,
    sort: CollectionSortBy,
  ) => {
    const sortedCollection = sortCollection(collection, sort);

    setPlaybackRange(null);
    setPlayingItem(sortedCollection);
    setCurrentlyPlayingIndex(startIndex);
    setPlaybackPosition(0);
    setPlayIntent(true);
  };

  const playClip = (
    clip: Clip,
    range?: { startMs: number; endMs: number },
  ) => {
    // A ranged clip's playback identity is (clip, window): the same clipId
    // trimmed to a different [startMs, endMs] is a different thing to play.
    // Only toggle play/pause when both match — otherwise an edit to the trim
    // window would replay the stale range.
    const isSameClip =
      !!playingItem &&
      !('clips' in playingItem) &&
      playingItem.clipId === clip.clipId;
    const isSameRange =
      (playbackRange?.startMs ?? null) === (range?.startMs ?? null) &&
      (playbackRange?.endMs ?? null) === (range?.endMs ?? null);
    if (isSameClip && isSameRange) {
      togglePlayback();
      return;
    }
    setPlaybackRange(range ?? null);
    setCurrentlyPlayingIndex(-1);
    setPlayingItem(clip);
    setPlaybackPosition(range ? range.startMs / 1000 : 0);
    setPlayIntent(true);
  };

  const togglePlayback = () => {
    setPlayIntent(!playIntent);
  };

  const clickCollection = (collection: Collection, sort: CollectionSortBy) => {
    // If this collection is already playing, toggle playback (pause/resume)
    if (
      playingItem &&
      'collectionId' in playingItem &&
      playingItem.collectionId === collection.collectionId
    ) {
      togglePlayback();
    } else {
      // Play this collection
      playCollection(collection, 0, sort);
    }
  };

  const clickCollectionClip = (
    collection: Collection,
    index: number,
    sort: CollectionSortBy,
  ) => {
    // If this collection is already playing and this index is the current one, toggle playback (pause/resume)
    if (
      playingItem &&
      'collectionId' in playingItem &&
      playingItem.collectionId === collection.collectionId &&
      currentlyPlayingIndex === index
    ) {
      togglePlayback();
    } else {
      // Play this collection starting from the specified index
      playCollection(collection, index, sort);
    }
  };

  const seek = (position: number) => {
    setPlaybackPosition(position);
    if (audioRef.current) {
      audioRef.current.currentTime = position;
    }
  };

  const next = () => {
    if (
      playingItem &&
      'clips' in playingItem &&
      currentlyPlayingIndex < playingItem.clips.length - 1
    ) {
      setCurrentlyPlayingIndex(currentlyPlayingIndex + 1);
      setPlaybackPosition(0);
    }
  };

  const prev = () => {
    if (playingItem && 'clips' in playingItem && currentlyPlayingIndex > 0) {
      setCurrentlyPlayingIndex(currentlyPlayingIndex - 1);
      setPlaybackPosition(0);
    }
  };

  const hasNext = () => {
    return (
      playingItem !== null &&
      'clips' in playingItem &&
      currentlyPlayingIndex < playingItem.clips.length - 1
    );
  };

  const hasPrev = () => {
    return (
      playingItem !== null &&
      'clips' in playingItem &&
      currentlyPlayingIndex > 0
    );
  };

  const isClipPlaying = (clipId: number) => {
    if (!playingItem) return false;

    if ('clips' in playingItem) {
      // Playing a collection - check if this clip is in the collection and is the current one
      return (
        playingItem.clips.some((track) => track.clipId === clipId) &&
        currentlyPlayingIndex >= 0 &&
        playingItem.clips[currentlyPlayingIndex]?.clipId === clipId
      );
    } else {
      // Playing a single clip
      return playingItem.clipId === clipId;
    }
  };

  const isCollectionPlaying = (collectionId: number) => {
    return (
      playingItem !== null &&
      'clips' in playingItem &&
      playingItem.collectionId === collectionId
    );
  };

  // Report position/duration relative to the clip window when one is active, so
  // the footer's progress bar spans the clip rather than the whole source file.
  const rangeStartSec = playbackRange ? playbackRange.startMs / 1000 : 0;
  const rangeDurationSec = playbackRange
    ? (playbackRange.endMs - playbackRange.startMs) / 1000
    : duration;
  const displayPosition = playbackRange
    ? Math.min(rangeDurationSec, Math.max(0, playbackPosition - rangeStartSec))
    : playbackPosition;
  const seekInRange = playbackRange
    ? (rel: number) => seek(rangeStartSec + rel)
    : seek;

  return {
    playingItem,
    playIntent,
    playbackPosition: displayPosition,
    currentlyPlayingIndex,
    volume,
    duration: rangeDurationSec,
    currentTrack,
    setVolume,
    togglePlayback,
    clickCollection,
    clickCollectionClip,
    playClip,
    seek: seekInRange,
    next,
    prev,
    hasNext,
    hasPrev,
    isClipPlaying,
    isCollectionPlaying,
  };
};
